/* eslint-disable no-empty-function */
import { AjaxResponse, ajax } from 'rxjs/ajax';
import {
  ApplyPromoCodeToCartApiResponse,
  Complete3DSCartApiResponse,
  CompleteCartDto,
  CreateCartAPIResponse,
  CreateCartDto,
  GetCartApiResponse,
  PromoCodeCartRequestParams,
  RemovePromoCodeFromCartApiResponse,
  SubmitCartApiResponse,
  SubmitOrderDto,
} from 'store/cart/types';
import {
  AvailableServiceResponse,
  AvailableServicesResponse,
  SERVICE_TYPE,
  ServiceTimeSlot,
  ServiceTypesResponse,
} from 'store/availableServices/types';
import { CreateContentDeepLinkParams, CreateContentDeepLinkResponse } from 'store/serviceContentDeeplink/types';
import {
  CreateServiceBookingForWaitlistResponse,
  PatchServiceBookingStatusResponse,
  ServiceBookingsResponse,
} from 'store/serviceBookings/types';
import {
  GetTransactionResponse,
  GetTransactionsAPIResponse,
  PatchTransactionDto,
  RedirectTransactionResponse,
} from 'store/transactions/types';
import { RegisterUser, RegistrationFieldsResponse } from 'store/serviceRegistration/types';
import {
  SavePaymentMethodRequest,
  SavePaymentMethodResponse,
  SavedPaymentMethod,
  UpdatePaymentMethodRequest,
} from 'store/payment/types';
import { ServiceContentRequest, ServiceContentResponse } from 'store/serviceContent/types';
import { ServiceProviderResponse, ServiceProvidersResponse } from 'store/serviceProviders/types';
import { TenantPortalFieldsEnum, TenantPortalsResponse } from 'store/tenantPortals/types';
import qs, { stringify } from 'qs';

import { API_PATHS } from 'shared/consts/paths';
import { BuildingResponse } from 'store/building/types';
import { BuildingTheme } from 'store/theme/types';
import { CurrentUserResponse } from 'store/user/types';
import { GetQrCodeRequest } from 'store/qr-codes/types';
import { Memberships } from 'store/serviceMemberships/types';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PATCH_STATUSES } from 'shared/consts';
import { validateJsonResponse } from './utils/validate-response.util';
import { InvalidResponseError } from './errors/invalid-response.error';

const handleSyntaxError = (error: unknown): Observable<never> => {
  if (error instanceof SyntaxError) {
    return throwError(
      () => new InvalidResponseError('Failed to parse service availability response', 0, null, error.message),
    );
  }
  return throwError(() => error);
};

export class ApiClient {
  constructor(
    private baseUrl: string,
    private authToken?: string | null,
    _buildingUuid?: string | null,
    private userBuildingUuid?: string | null,
    private locale?: string | null,
  ) {}

  private get DefaultHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      Authorization: this.authToken,
      'Hqo-Building-UUID': this.userBuildingUuid,
    };
  }

  private get DefaultParams(): Record<string, string> {
    return {
      lang: this.locale,
    };
  }

  getCurrentUser(): Observable<AjaxResponse<CurrentUserResponse>> {
    return ajax.get(`${this.baseUrl}/api/users/current`, this.DefaultHeaders);
  }

  getBuildingTheme(buildingUuid: string): Observable<AjaxResponse<BuildingTheme>> {
    return ajax.get(`${this.baseUrl}/api/buildings/${buildingUuid}/theme`, this.DefaultHeaders);
  }

  getTenantPortals(buildingUuid: string): Observable<AjaxResponse<TenantPortalsResponse>> {
    const queryString = qs.stringify(
      {
        fields: [TenantPortalFieldsEnum.SUBDOMAIN, TenantPortalFieldsEnum.UUID, TenantPortalFieldsEnum.ENABLED],
      },
      { skipNulls: true, encode: false },
    );

    return ajax.get(`${this.baseUrl}/api/buildings/${buildingUuid}/tenant-portals?${queryString}`, this.DefaultHeaders);
  }

  getBuilding(buildingUuid: string): Observable<AjaxResponse<BuildingResponse>> {
    return ajax.get(`${this.baseUrl}/api/tenant/v1/buildings/${buildingUuid}`, this.DefaultHeaders);
  }

  getServiceProviders(ownerType: string, ownerUuid: string): Observable<AjaxResponse<ServiceProvidersResponse>> {
    return ajax.get(
      `${this.baseUrl}/api${API_PATHS.SERVICE_BOOKING}/${ownerType}/${ownerUuid}/providers`,
      this.DefaultHeaders,
    );
  }

  getServiceProvidersV3(ownerType: string, ownerUuid: string): Observable<AjaxResponse<ServiceProvidersResponse>> {
    const queryString = qs.stringify(this.DefaultParams, { skipNulls: true, encode: false });
    return ajax.get(
      `${this.baseUrl}/api${API_PATHS.SERVICE_BOOKING_V3}/${ownerType}/${ownerUuid}/providers?${queryString}`,
      this.DefaultHeaders,
    );
  }

  getCurrentServiceProviderV3(
    ownerType: string,
    ownerUuid: string,
    companyUuid: string,
  ): Observable<AjaxResponse<ServiceProviderResponse>> {
    const queryString = qs.stringify(this.DefaultParams, { skipNulls: true, encode: false });
    return ajax.get(
      `${this.baseUrl}/api${API_PATHS.SERVICE_BOOKING_V3}/${ownerType}/${ownerUuid}/providers/${companyUuid}?${queryString}`,
      this.DefaultHeaders,
    );
  }

  getRegistrationFields(ownerType: string, ownerUuid: string): Observable<AjaxResponse<RegistrationFieldsResponse>> {
    return ajax.get(
      `${this.baseUrl}/api/service-booking/v2/${ownerType}/${ownerUuid}/registration`,
      this.DefaultHeaders,
    );
  }

  getRegistrationFieldsV3(
    ownerType: string,
    ownerUuid: string,
    companyUuid: string,
  ): Observable<AjaxResponse<RegistrationFieldsResponse>> {
    const queryString = qs.stringify(this.DefaultParams, { skipNulls: true, encode: false });
    return ajax.get(
      `${this.baseUrl}/api${API_PATHS.SERVICE_BOOKING_V3}/${ownerType}/${ownerUuid}/providers/${companyUuid}/registration?${queryString}`,
      this.DefaultHeaders,
    );
  }

  registerUser({ ownerType, ownerUuid, registerUserDto }: RegisterUser): Observable<AjaxResponse<unknown>> {
    return ajax.post(
      `${this.baseUrl}/api/service-booking/v2/${ownerType}/${ownerUuid}/registration`,
      { user_info: registerUserDto },
      this.DefaultHeaders,
    );
  }

  registerUserV3({
    ownerType,
    ownerUuid,
    companyUuid,
    registerUserDto,
  }: RegisterUser): Observable<AjaxResponse<unknown>> {
    return ajax.post(
      `${this.baseUrl}/api${API_PATHS.SERVICE_BOOKING_V3}/${ownerType}/${ownerUuid}/providers/${companyUuid}/registration`,
      { user_info: registerUserDto },
      this.DefaultHeaders,
    );
  }

  getMemberships(ownerType: string, ownerUuid: string): Observable<AjaxResponse<Memberships>> {
    return ajax.get(
      `${this.baseUrl}/api/service-booking/v2/${ownerType}/${ownerUuid}/memberships`,
      this.DefaultHeaders,
    );
  }

  getMembershipsV3(ownerType: string, ownerUuid: string, companyUuid: string): Observable<AjaxResponse<Memberships>> {
    const queryString = qs.stringify(this.DefaultParams, { skipNulls: true, encode: false });
    return ajax.get(
      `${this.baseUrl}/api${API_PATHS.SERVICE_BOOKING_V3}/${ownerType}/${ownerUuid}/providers/${companyUuid}/memberships?${queryString}`,
      this.DefaultHeaders,
    );
  }

  getQrCode(params: GetQrCodeRequest): Observable<AjaxResponse<string>> {
    const { content, imageWidth, errorCorrectionLevel } = params;

    const query = qs.stringify(
      {
        format: 'svg',
        width: imageWidth,
        content,
        errorCorrectionLevel,
      },
      {
        skipNulls: true,
        encode: true,
      },
    );

    return ajax({
      method: 'GET',
      url: `${this.baseUrl}/qr-codes/public/v1/qr?${query}`,
      responseType: 'text',
      headers: {
        ...this.DefaultHeaders,
        'Content-Type': 'image/svg+xml',
      },
    });
  }

  getMineServiceBookings(
    ownerType: string,
    ownerUuid: string,
    companyUuid: string,
    serviceUuid: string,
  ): Observable<AjaxResponse<ServiceBookingsResponse>> {
    const queryString = qs.stringify(this.DefaultParams, { skipNulls: true, encode: false });
    return ajax.get(
      `${this.baseUrl}/api${API_PATHS.SERVICE_BOOKING_V3}/${ownerType}/${ownerUuid}/providers/${companyUuid}/services/${serviceUuid}/bookings/mine?${queryString}`,
      this.DefaultHeaders,
    );
  }

  patchServiceBookingStatus(
    ownerType: string,
    ownerUuid: string,
    companyUuid: string,
    serviceUuid: string,
    bookingId: number,
    status: PATCH_STATUSES,
  ): Observable<AjaxResponse<PatchServiceBookingStatusResponse>> {
    const body = {
      booking: {
        status,
      },
    };
    return ajax.patch(
      `${this.baseUrl}/api${API_PATHS.SERVICE_BOOKING_V3}/${ownerType}/${ownerUuid}/providers/${companyUuid}/services/${serviceUuid}/bookings/${bookingId}`,
      body,
      this.DefaultHeaders,
    );
  }

  createServiceBookingForWaitlist(
    ownerType: string,
    ownerUuid: string,
    companyUuid: string,
    serviceUuid: string,
    startAt: string,
    endAt: string,
    timeSlotId?: number,
  ): Observable<AjaxResponse<CreateServiceBookingForWaitlistResponse>> {
    const body = {
      booking: {
        status: PATCH_STATUSES.WAITLIST,
        start_at: startAt,
        end_at: endAt,
        time_slot_id: timeSlotId || null,
      },
    };
    return ajax.post(
      `${this.baseUrl}/api${API_PATHS.SERVICE_BOOKING_V3}/${ownerType}/${ownerUuid}/providers/${companyUuid}/services/${serviceUuid}/bookings`,
      body,
      this.DefaultHeaders,
    );
  }

  getAvailableServices(
    ownerType: string,
    ownerUuid: string,
    availability_start?: string,
    availability_end?: string,
    service_type_id?: string,
  ): Observable<AjaxResponse<AvailableServicesResponse>> {
    const url = `${this.baseUrl}/api/service-booking/v2/${ownerType}/${ownerUuid}/services?${qs.stringify(
      { availability_start, availability_end, service_type_id },
      { skipNulls: true, encode: false },
    )}`;

    return ajax.get(url, this.DefaultHeaders);
  }

  getAvailableServicesV3(
    ownerType: string,
    ownerUuid: string,
    companyUuid: string,
    availability_start?: string,
    availability_end?: string,
    service_type_id?: string,
  ): Observable<AjaxResponse<AvailableServicesResponse>> {
    const url = `${this.baseUrl}/api${
      API_PATHS.SERVICE_BOOKING_V3
    }/${ownerType}/${ownerUuid}/providers/${companyUuid}/services?${qs.stringify(
      { ...this.DefaultParams, availability_start, availability_end, service_type_id },
      { skipNulls: true, encode: false },
    )}`;
    return ajax.get(url, this.DefaultHeaders);
  }

  getCurrentAvailableService(
    ownerType: string,
    ownerUuid: string,
    serviceUuid: string,
    availability_start: string,
    availability_end: string,
    service_type_id?: string,
  ): Observable<AjaxResponse<AvailableServiceResponse>> {
    const url = `${
      this.baseUrl
    }/api/service-booking/v2/${ownerType}/${ownerUuid}/services/${serviceUuid}?${qs.stringify(
      { availability_start, availability_end, service_type_id },
      { skipNulls: true, encode: false },
    )}`;

    return ajax.get<AvailableServiceResponse>(url, this.DefaultHeaders).pipe(
      map(response => validateJsonResponse(response)),
      catchError(handleSyntaxError),
    );
  }

  getCurrentAvailableServiceV3(
    ownerType: string,
    ownerUuid: string,
    companyUuid: string,
    serviceUuid: string,
    availability_start: string,
    availability_end: string,
    service_type_id?: string,
  ): Observable<AjaxResponse<AvailableServiceResponse>> {
    // SMB-547: merge `lang=` (DefaultParams) into the same qs.stringify call so helix
    // can resolve per-device translations and the malformed `?&include=…` artifact disappears.
    const queryString = qs.stringify(
      {
        ...this.DefaultParams,
        availability_start,
        availability_end,
        service_type_id,
        include: ['translations', 'discounts'],
      },
      { skipNulls: true, arrayFormat: 'comma', encode: false },
    );

    const url = `${this.baseUrl}/api${API_PATHS.SERVICE_BOOKING_V3}/${ownerType}/${ownerUuid}/providers/${companyUuid}/services/${serviceUuid}?${queryString}`;

    return ajax.get<AvailableServiceResponse>(url, this.DefaultHeaders).pipe(
      map(response => validateJsonResponse(response)),
      catchError(handleSyntaxError),
    );
  }

  getServiceTypes(
    ownerType: string,
    ownerUuid: string,
    serviceType?: SERVICE_TYPE,
  ): Observable<AjaxResponse<ServiceTypesResponse>> {
    const url = `${this.baseUrl}/api/service-booking/v2/${ownerType}/${ownerUuid}/servicetypes?${qs.stringify(
      { serviceType },
      { skipNulls: true },
    )}`;
    return ajax.get(url, this.DefaultHeaders);
  }

  getServiceTypesV3(
    ownerType: string,
    ownerUuid: string,
    companyUuid: string,
  ): Observable<AjaxResponse<ServiceTypesResponse>> {
    const queryString = qs.stringify(this.DefaultParams, { skipNulls: true, encode: false });
    const url = `${this.baseUrl}/api${API_PATHS.SERVICE_BOOKING_V3}/${ownerType}/${ownerUuid}/providers/${companyUuid}/servicetypes?${queryString}`;
    return ajax.get(url, this.DefaultHeaders);
  }

  getServiceTimeSlotsV3(
    ownerType: string,
    ownerUuid: string,
    companyUuid: string,
    serviceUuid: string,
  ): Observable<AjaxResponse<ServiceTimeSlot[]>> {
    const queryString = qs.stringify(this.DefaultParams, { skipNulls: true, encode: false });
    const url = `${this.baseUrl}/api${API_PATHS.SERVICE_BOOKING_V3}/${ownerType}/${ownerUuid}/providers/${companyUuid}/services/${serviceUuid}/time-slots?${queryString}`;
    return ajax.get(url, this.DefaultHeaders);
  }

  getServiceContent({
    buildingUuid,
    contentUuid,
  }: ServiceContentRequest): Observable<AjaxResponse<ServiceContentResponse>> {
    const url = `${this.baseUrl}/api/tenant/v2/buildings/${buildingUuid}/content/${contentUuid}`;
    return ajax.get(url, this.DefaultHeaders);
  }

  createServiceDeepLink(params: CreateContentDeepLinkParams): Observable<AjaxResponse<CreateContentDeepLinkResponse>> {
    const body: Record<string, unknown> = {};
    const { buildingUuid, contentUuid, navigation_command, default_redirect } = params;

    if (navigation_command) {
      body.navigation_command = { ...navigation_command };
    }

    if (default_redirect) {
      body.default_redirect = default_redirect;
    }

    return ajax.post(
      `${this.baseUrl}/api/admin/v2/buildings/${buildingUuid}/content/${contentUuid}/deeplink`,
      body,
      this.DefaultHeaders,
    );
  }

  createCart(createCartDto: CreateCartDto): Observable<AjaxResponse<CreateCartAPIResponse>> {
    const { app_instance_config_uuid, ...dto } = createCartDto;

    return ajax.post(
      `${this.baseUrl}/api/payments/v1/carts?${stringify({
        appInstanceConfigUuid: app_instance_config_uuid,
      })}`,
      dto,
      this.DefaultHeaders,
    );
  }

  getTransactions(serviceProviderUuid?: string): Observable<AjaxResponse<GetTransactionsAPIResponse>> {
    const extraParams = stringify(
      { service_provider_uuid: serviceProviderUuid },
      { skipNulls: true, addQueryPrefix: false },
    );
    const trailingParams = extraParams ? `&${extraParams}` : '';

    return ajax.get(
      `${this.baseUrl}/api/tenant/v1/users/current/transactions?types=services_booking&is_upcoming=true&statuses=waitlisted,confirmed,Booked,checked_in${trailingParams}`,
      this.DefaultHeaders,
    );
  }

  patchTransaction(patchTransactionDto: PatchTransactionDto) {
    const { transactionId, ...body } = patchTransactionDto;

    return ajax.patch(`${this.baseUrl}/api/transactions/${transactionId}`, body, this.DefaultHeaders);
  }

  getCart(cartId: string): Observable<AjaxResponse<GetCartApiResponse>> {
    return ajax.get(`${this.baseUrl}/api/payments/v1/carts/${cartId}`, this.DefaultHeaders);
  }

  submitCart(submitCartDto: SubmitOrderDto): Observable<AjaxResponse<SubmitCartApiResponse>> {
    const params = stringify(
      submitCartDto.locationId
        ? { locationId: submitCartDto.locationId, ...this.DefaultParams }
        : { ...this.DefaultParams },
    );

    return ajax.post(
      `${this.baseUrl}/api/payments/v1/carts/${submitCartDto.cart_id}?${params}`,
      { ...submitCartDto, cart_id: undefined, locationId: undefined },
      this.DefaultHeaders,
    );
  }

  applyPromoCodeToCart(payload: PromoCodeCartRequestParams): Observable<AjaxResponse<ApplyPromoCodeToCartApiResponse>> {
    return ajax.post(
      `${this.baseUrl}/api/payments/v1/carts/${payload.cartId}/promotions/${payload.promoCode}`,
      null,
      this.DefaultHeaders,
    );
  }

  removePromoCodeFromCart(
    payload: PromoCodeCartRequestParams,
  ): Observable<AjaxResponse<RemovePromoCodeFromCartApiResponse>> {
    return ajax.delete(
      `${this.baseUrl}/api/payments/v1/carts/${payload.cartId}/promotions/${payload.promoCode}`,
      this.DefaultHeaders,
    );
  }

  complete3DSCart(payload: CompleteCartDto): Observable<AjaxResponse<Complete3DSCartApiResponse>> {
    return ajax.post(`${this.baseUrl}/api/payments/complete-3ds`, payload, this.DefaultHeaders);
  }

  deletePaymentMethod(cardId: string): Observable<AjaxResponse<[undefined, undefined]>> {
    return ajax.delete(`${this.baseUrl}/api/payment-methods/${cardId}`, this.DefaultHeaders);
  }

  updatePaymentMethod(
    updatePaymentMethodRequest: UpdatePaymentMethodRequest,
  ): Observable<AjaxResponse<SavePaymentMethodResponse>> {
    return ajax.patch(
      `${this.baseUrl}/api/payment-methods/${updatePaymentMethodRequest.card_id}`,
      { default: updatePaymentMethodRequest.default },
      this.DefaultHeaders,
    );
  }

  getPaymentMethods(cartId: string): Observable<AjaxResponse<Array<SavedPaymentMethod>>> {
    const params = stringify({ cartId });

    return ajax.get(`${this.baseUrl}/api/payment-methods?${params}`, this.DefaultHeaders);
  }

  savePaymentMethod(
    savePaymentMethodRequest: SavePaymentMethodRequest,
  ): Observable<AjaxResponse<SavePaymentMethodResponse>> {
    return ajax.post(`${this.baseUrl}/api/payment-methods`, savePaymentMethodRequest, this.DefaultHeaders);
  }

  getRedirectTransaction(transactionUuid: string): Observable<AjaxResponse<RedirectTransactionResponse>> {
    return ajax.get(`${this.baseUrl}/api/payments/transaction/${transactionUuid}`, this.DefaultHeaders);
  }

  getTransaction(transactionUuid: string): Observable<AjaxResponse<GetTransactionResponse>> {
    return ajax.get(`${this.baseUrl}/api/tenant/v1/users/current/transactions/${transactionUuid}`, this.DefaultHeaders);
  }
}
